import React, { useState } from 'react';

interface StatData {
  label: string;
  value: number;
  change: number;
  icon: string;
  color: string;
}

interface ChartData {
  month: string;
  visits: number;
  sales: number;
  products: number;
}

const MisEstadisticas: React.FC = () => {
  const [timeRange, setTimeRange] = useState('30d');

  const statsData: StatData[] = [
    {
      label: 'Visitas Totales',
      value: 15420,
      change: 12.5,
      icon: 'fas fa-eye',
      color: '#667eea'
    },
    {
      label: 'Ventas Realizadas',
      value: 847,
      change: 8.2,
      icon: 'fas fa-shopping-cart',
      color: '#48bb78'
    },
    {
      label: 'Productos Vendidos',
      value: 1234,
      change: -2.1,
      icon: 'fas fa-box',
      color: '#ed8936'
    },
    {
      label: 'Ingresos Totales',
      value: 1250000,
      change: 15.7,
      icon: 'fas fa-dollar-sign',
      color: '#f56565'
    },
    {
      label: 'Clientes Nuevos',
      value: 234,
      change: 23.4,
      icon: 'fas fa-user-plus',
      color: '#9f7aea'
    },
    {
      label: 'Tasa de Conversión',
      value: 5.5,
      change: 1.2,
      icon: 'fas fa-percentage',
      color: '#38b2ac'
    }
  ];

  const chartData: ChartData[] = [
    { month: 'Ene', visits: 1200, sales: 45, products: 89 },
    { month: 'Feb', visits: 1350, sales: 52, products: 95 },
    { month: 'Mar', visits: 1100, sales: 38, products: 76 },
    { month: 'Abr', visits: 1600, sales: 67, products: 112 },
    { month: 'May', visits: 1800, sales: 78, products: 134 },
    { month: 'Jun', visits: 2100, sales: 89, products: 156 },
    { month: 'Jul', visits: 1950, sales: 82, products: 143 },
    { month: 'Ago', visits: 2200, sales: 95, products: 167 },
    { month: 'Sep', visits: 2400, sales: 103, products: 178 },
    { month: 'Oct', visits: 2600, sales: 112, products: 189 },
    { month: 'Nov', visits: 2800, sales: 121, products: 201 },
    { month: 'Dic', visits: 3000, sales: 130, products: 215 }
  ];

  const topProducts = [
    { name: 'Laptop Gaming Pro', sales: 45, revenue: 6750000 },
    { name: 'Smartphone Galaxy S24', sales: 38, revenue: 3230000 },
    { name: 'Auriculares Bluetooth Premium', sales: 67, revenue: 1675000 },
    { name: 'Tablet iPad Pro', sales: 23, revenue: 3450000 },
    { name: 'Monitor 4K Gaming', sales: 19, revenue: 2850000 }
  ];

  const recentActivity = [
    { action: 'Nueva venta realizada', detail: 'Laptop Gaming Pro - $150,000', time: 'Hace 2 minutos', type: 'sale' },
    { action: 'Cliente nuevo registrado', detail: 'María González', time: 'Hace 15 minutos', type: 'user' },
    { action: 'Producto agregado', detail: 'Monitor 4K Gaming', time: 'Hace 1 hora', type: 'product' },
    { action: 'Pago recibido', detail: 'Smartphone Galaxy S24 - $85,000', time: 'Hace 2 horas', type: 'payment' },
    { action: 'Reseña publicada', detail: '5 estrellas - Laptop Gaming Pro', time: 'Hace 3 horas', type: 'review' }
  ];

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const getMaxValue = (data: ChartData[], key: keyof ChartData) => {
    return Math.max(...data.map(d => d[key] as number));
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'sale': return 'fas fa-shopping-cart';
      case 'user': return 'fas fa-user-plus';
      case 'product': return 'fas fa-box';
      case 'payment': return 'fas fa-credit-card';
      case 'review': return 'fas fa-star';
      default: return 'fas fa-info-circle';
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'sale': return '#48bb78';
      case 'user': return '#667eea';
      case 'product': return '#ed8936';
      case 'payment': return '#38b2ac';
      case 'review': return '#f6ad55';
      default: return '#a0aec0';
    }
  };

  return (
    <div className="mis-estadisticas-section">
      <div className="section-header">
        <h2>Mis Estadísticas</h2>
        <p>Analiza el rendimiento de tu negocio y toma decisiones informadas</p>
      </div>

      {/* Time Range Selector */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Período de Análisis</h3>
          <div className="time-range-selector">
            <button 
              className={`btn ${timeRange === '7d' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTimeRange('7d')}
            >
              7 días
            </button>
            <button 
              className={`btn ${timeRange === '30d' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTimeRange('30d')}
            >
              30 días
            </button>
            <button 
              className={`btn ${timeRange === '90d' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTimeRange('90d')}
            >
              90 días
            </button>
            <button 
              className={`btn ${timeRange === '1y' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTimeRange('1y')}
            >
              1 año
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid-3" style={{ marginBottom: '2rem' }}>
        {statsData.map((stat, index) => (
          <div key={index} className="dashboard-card">
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: stat.color + '20', color: stat.color }}>
                <i className={stat.icon}></i>
              </div>
              <div className="stat-content">
                <h3>{formatNumber(stat.value)}</h3>
                <p>{stat.label}</p>
                <span className={`stat-change ${stat.change >= 0 ? 'positive' : 'negative'}`}>
                  <i className={`fas fa-arrow-${stat.change >= 0 ? 'up' : 'down'}`}></i>
                  {Math.abs(stat.change)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid-2" style={{ marginBottom: '2rem' }}>
        {/* Visits Chart */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Visitas Mensuales</h3>
          </div>
          <div className="chart-container">
            <div className="chart-bars">
              {chartData.map((data, index) => {
                const maxVisits = getMaxValue(chartData, 'visits');
                const height = (data.visits / maxVisits) * 100;
                return (
                  <div key={index} className="chart-bar-container">
                    <div 
                      className="chart-bar"
                      style={{ 
                        height: `${height}%`,
                        backgroundColor: '#667eea'
                      }}
                    >
                      <span className="chart-value">{formatNumber(data.visits)}</span>
                    </div>
                    <span className="chart-label">{data.month}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sales Chart */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Ventas Mensuales</h3>
          </div>
          <div className="chart-container">
            <div className="chart-bars">
              {chartData.map((data, index) => {
                const maxSales = getMaxValue(chartData, 'sales');
                const height = (data.sales / maxSales) * 100;
                return (
                  <div key={index} className="chart-bar-container">
                    <div 
                      className="chart-bar"
                      style={{ 
                        height: `${height}%`,
                        backgroundColor: '#48bb78'
                      }}
                    >
                      <span className="chart-value">{data.sales}</span>
                    </div>
                    <span className="chart-label">{data.month}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Top Products and Recent Activity */}
      <div className="grid-2" style={{ marginBottom: '2rem' }}>
        {/* Top Products */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Productos Más Vendidos</h3>
          </div>
          <div className="top-products-list">
            {topProducts.map((product, index) => (
              <div key={index} className="product-item">
                <div className="product-rank">#{index + 1}</div>
                <div className="product-info">
                  <h4>{product.name}</h4>
                  <p>{product.sales} ventas • {formatCurrency(product.revenue)}</p>
                </div>
                <div className="product-percentage">
                  {Math.round((product.sales / topProducts.reduce((sum, p) => sum + p.sales, 0)) * 100)}%
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Actividad Reciente</h3>
          </div>
          <div className="activity-list">
            {recentActivity.map((activity, index) => (
              <div key={index} className="activity-item">
                <div 
                  className="activity-icon"
                  style={{ backgroundColor: getActivityColor(activity.type) + '20', color: getActivityColor(activity.type) }}
                >
                  <i className={getActivityIcon(activity.type)}></i>
                </div>
                <div className="activity-content">
                  <h4>{activity.action}</h4>
                  <p>{activity.detail}</p>
                  <span className="activity-time">{activity.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Métricas de Rendimiento</h3>
        </div>
        <div className="metrics-grid">
          <div className="metric-item">
            <h4>Tasa de Conversión</h4>
            <div className="metric-value">5.5%</div>
            <div className="metric-progress">
              <div className="progress-bar" style={{ width: '55%', backgroundColor: '#48bb78' }}></div>
            </div>
          </div>
          <div className="metric-item">
            <h4>Tiempo Promedio en Sitio</h4>
            <div className="metric-value">4m 32s</div>
            <div className="metric-progress">
              <div className="progress-bar" style={{ width: '75%', backgroundColor: '#667eea' }}></div>
            </div>
          </div>
          <div className="metric-item">
            <h4>Tasa de Rebote</h4>
            <div className="metric-value">32.4%</div>
            <div className="metric-progress">
              <div className="progress-bar" style={{ width: '32%', backgroundColor: '#ed8936' }}></div>
            </div>
          </div>
          <div className="metric-item">
            <h4>Valor Promedio de Compra</h4>
            <div className="metric-value">{formatCurrency(147500)}</div>
            <div className="metric-progress">
              <div className="progress-bar" style={{ width: '85%', backgroundColor: '#f56565' }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MisEstadisticas;
